/**
 * push.js
 * Turning notifications on and off for the device you are using.
 *
 * The browser does the hard part. This asks for permission, registers the
 * service worker, creates a subscription with the server's public key, and
 * hands the subscription to the server so it knows where to send.
 *
 * Two traps worth knowing about:
 *
 * **iPhone and iPad only allow it from the Home Screen.** Safari on iOS will
 * not offer push to a website in a tab — Kreeboom has to be added to the Home
 * Screen and opened from there. That is detected and explained rather than
 * left to fail.
 *
 * **A subscription is tied to the key it was made with.** If the server's key
 * ever changes, old subscriptions silently stop receiving. Turning on again
 * notices a mismatch and replaces the subscription.
 */

import { api } from '../services/api.js';

/** Can this browser do push at all, and if not, why not. */
export function pushSupport() {
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const installed =
    window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return { ok: false, reason: ios && !installed ? 'ios-needs-install' : 'unsupported' };
  }
  return { ok: true };
}

/** 'granted', 'denied', 'default', or 'unsupported'. */
export const permission = () => ('Notification' in window ? Notification.permission : 'unsupported');

const toBytes = (b64) => {
  const s = String(b64).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(s + '='.repeat((4 - (s.length % 4)) % 4));
  return Uint8Array.from(bin, (ch) => ch.charCodeAt(0));
};

const sameKey = (buffer, b64) => {
  if (!buffer) return false;
  const a = new Uint8Array(buffer);
  const b = toBytes(b64);
  return a.length === b.length && a.every((v, i) => v === b[i]);
};

/** "Chrome on Windows" — so the list of devices is readable. */
function deviceLabel() {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Browser';
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /iPhone|iPad|iPod/.test(ua)
      ? 'iPhone'
      : /Android/.test(ua)
        ? 'Android'
        : /Mac OS X/.test(ua)
          ? 'Mac'
          : /Linux/.test(ua)
            ? 'Linux'
            : 'this device';
  return `${browser} on ${os}`;
}

export async function currentSubscription() {
  if (!pushSupport().ok) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
}

/**
 * Asks permission, subscribes, and tells the server.
 * Throws an Error whose message can be shown as it is.
 */
export async function turnOn() {
  const support = pushSupport();
  if (!support.ok) {
    throw new Error(
      support.reason === 'ios-needs-install'
        ? 'On an iPhone or iPad, add Kreeboom to your Home Screen first, open it from there, then turn this on.'
        : 'This browser cannot show notifications from a website.',
    );
  }

  const { configured, publicKey } = await api.notifications.pushStatus();
  if (!configured || !publicKey) throw new Error('Notifications are not set up on the server yet.');

  const answer = await Notification.requestPermission();
  if (answer !== 'granted') {
    throw new Error(
      answer === 'denied'
        ? 'Notifications are blocked for this site. Allow them in the browser’s site settings (the icon left of the address), then try again.'
        : 'Notifications were not allowed.',
    );
  }

  const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub.options?.applicationServerKey, publicKey)) {
    await sub.unsubscribe();
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: toBytes(publicKey),
    });
  }

  const json = sub.toJSON();
  await api.notifications.subscribe({ endpoint: json.endpoint, keys: json.keys, label: deviceLabel() });
  return sub;
}

/** Stops notifications on this device, and tells the server to forget it. */
export async function turnOff() {
  const sub = await currentSubscription();
  if (!sub) return;
  try {
    await api.notifications.unsubscribe({ endpoint: sub.endpoint });
  } finally {
    await sub.unsubscribe();
  }
}
